/**
 * Customer PWA client-state cleanup — Cache Storage + SW unregister.
 * Used by Settings → Update and logout-adjacent recovery paths.
 */

/** Max wait for SW controller release after unregister before navigation proceeds. */
export const CUSTOMER_PWA_SW_RELEASE_TIMEOUT_MS = 2_000;

export const CUSTOMER_PWA_SW_RELEASE_POLL_MS = 50;

export interface ClearCustomerPwaClientStateOptions {
  readonly waitForControllerRelease?: boolean;
  readonly swReleaseTimeoutMs?: number;
}

export interface WaitForCustomerServiceWorkerReleaseResult {
  readonly released: boolean;
  readonly hadController: boolean;
  readonly registrationCount: number;
}

async function clearAllCaches(): Promise<void> {
  if (typeof caches === 'undefined') {
    return;
  }
  try {
    const keys = await caches.keys();
    await Promise.all(keys.map((key) => caches.delete(key)));
  } catch {
    // Best-effort — update must not throw
  }
}

async function unregisterAllServiceWorkers(): Promise<void> {
  if (!('serviceWorker' in navigator)) {
    return;
  }
  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations.map((registration) => registration.unregister()));
  } catch {
    // Best-effort
  }
}

async function countServiceWorkerRegistrations(): Promise<number> {
  if (!('serviceWorker' in navigator) || navigator.serviceWorker == null) {
    return 0;
  }
  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    return registrations.length;
  } catch {
    return 0;
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

export async function waitForCustomerServiceWorkerRelease(options?: {
  readonly timeoutMs?: number;
  readonly pollMs?: number;
}): Promise<WaitForCustomerServiceWorkerReleaseResult> {
  const timeoutMs = options?.timeoutMs ?? CUSTOMER_PWA_SW_RELEASE_TIMEOUT_MS;
  const pollMs = options?.pollMs ?? CUSTOMER_PWA_SW_RELEASE_POLL_MS;
  const hadController =
    'serviceWorker' in navigator &&
    navigator.serviceWorker != null &&
    navigator.serviceWorker.controller != null;
  const deadline = Date.now() + timeoutMs;

  while (Date.now() < deadline) {
    const stillControlled =
      'serviceWorker' in navigator &&
      navigator.serviceWorker != null &&
      navigator.serviceWorker.controller != null;
    const registrationCount = await countServiceWorkerRegistrations();
    if (!stillControlled && registrationCount === 0) {
      return {
        released: true,
        hadController,
        registrationCount: 0,
      };
    }
    await sleep(pollMs);
  }

  return {
    released: false,
    hadController:
      'serviceWorker' in navigator &&
      navigator.serviceWorker != null &&
      navigator.serviceWorker.controller != null,
    registrationCount: await countServiceWorkerRegistrations(),
  };
}

export async function clearCustomerPwaClientState(
  options: ClearCustomerPwaClientStateOptions = {},
): Promise<WaitForCustomerServiceWorkerReleaseResult> {
  await Promise.all([clearAllCaches(), unregisterAllServiceWorkers()]);

  if (options.waitForControllerRelease === false) {
    return {
      released: true,
      hadController: false,
      registrationCount: await countServiceWorkerRegistrations(),
    };
  }

  return waitForCustomerServiceWorkerRelease({
    timeoutMs: options.swReleaseTimeoutMs,
  });
}
